import { useTranslation } from "react-i18next";
import React, {
  useRef,
  useEffect,
  useCallback,
  useContext,
  useMemo
} from "react";
import { useArrMrr } from "@/services/context/ArrMrrContext";
import {
  Draggable,
  Droppable,
  DroppableProvided,
  DraggableProvided,
  DraggableStateSnapshot
} from "@hello-pangea/dnd";
import { Column } from "@/pages/deals/components/KanbanBoard/KanbanBoard";
import StageCard from "@/components/controls/StageCard";
import DealCard from "@/pages/deals/components/KanbanBoard/DealCard/DealCard";
import { Expand } from "@dreamhub/designsystem/icons";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent
} from "@/components/ui/tooltip";
import { DealData } from "@/pages/deals/types";
import { getColorForMilestoneByType } from "@/components/shared/Milestones";
import IconContainer from "@/components/ui/icon-container";
import { useFilteredDeals, useAlertsBatch } from "@/pages/deals/hooks";
import {
  buildKanbanColumnFilters,
  isStageIncludedInFilter
} from "@/pages/deals/helpers";
import InlineLoader from "@/components/ui/inline-loader";
import { useInfiniteScrollTrigger } from "@/hooks/useInfiniteScrollTrigger";
import { ComputedFields, GenericAggregations } from "@/services/api/types";
import { DealFilters } from "@/pages/deals/components/DealsToolbar";
import { cn } from "@/utils/utils";
import { KanbanContext } from "@/pages/deals/components/KanbanBoard/KanbanProvider";
import { daysToWeeks } from "date-fns";
import { find, sumBy, meanBy } from "lodash";

/** Stable empty reference — a fresh [] would retrigger the merge effect. */
const NO_DEALS: DealData[] = [];

interface KanbanColumnProps {
  isMinimized: boolean;
  onExpandColumn: (columnId: number) => void;
  column: Column;
  onMinimizeColumn: (columnId: number) => void;
  draggedOverArea: string | null;
  sourceColumnIndex: number | null;
  onCardClick: (item: DealData | null) => void;
  addColumnDealsToMainState: (deals: DealData[], columnId: number) => void;
  filters: DealFilters;
  searchTerm: string;
  sortParams: {
    field: string;
    asc: boolean;
  };
  drawerDealId?: string;
}

const KanbanColumn: React.FC<KanbanColumnProps> = ({
  isMinimized,
  onExpandColumn,
  column,
  onMinimizeColumn,
  draggedOverArea,
  sourceColumnIndex,
  onCardClick,
  addColumnDealsToMainState,
  filters,
  searchTerm,
  sortParams,
  drawerDealId
}) => {
  const { t } = useTranslation();
  const { milestone, id, items: allItems, title } = column;

  const isStageInFilter = isStageIncludedInFilter(filters.stage, id);

  // Board state still holds this stage's deals for the commit in which the
  // filter starts excluding it — the merge effect only purges them afterwards.
  const stageItems = isStageInFilter ? allItems : NO_DEALS;

  const columnDealIds = useMemo(
    () => stageItems.map((deal) => deal.id),
    [stageItems]
  );

  const { data: alertsBatchData } = useAlertsBatch(
    columnDealIds,
    isStageInFilter && columnDealIds.length > 0
  );

  const items = useMemo(() => {
    if (!alertsBatchData?.alerts) return stageItems;
    return stageItems.map((item) => {
      const dealAlerts = alertsBatchData.alerts[item.id];
      if (!dealAlerts) return item;
      return { ...item, alerts: dealAlerts };
    });
  }, [stageItems, alertsBatchData]);
  const columnRef = useRef<HTMLDivElement | null>(null);
  const { fieldName: arrOrMrrField } = useArrMrr();

  const kanbanContext = useContext(KanbanContext);

  const columnFilters = useMemo(
    () => buildKanbanColumnFilters(filters, id),
    [filters, id]
  );

  // Search runs on the server so a short column never pages through its whole stage.
  const {
    entities: columnDeals,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    isError,
    total,
    isLoading,
    refetch,
    aggregations,
    isAggregationsLoading,
    isAggregationsError
  } = useFilteredDeals(
    columnFilters,
    searchTerm ? { term: searchTerm } : undefined,
    "stage", // Use to enforce aggregations
    undefined,
    sortParams.field,
    sortParams.asc,
    false,
    isStageInFilter,
    ComputedFields.MINIMAL
  );

  // A stage the filter excludes keeps no results at all. The query is disabled,
  // but the hook still replays its last cached page, so the values it returns
  // have to be discarded explicitly or the column keeps showing pre-filter deals.
  const stageDeals = isStageInFilter ? (columnDeals as DealData[]) : NO_DEALS;
  const stageTotal = isStageInFilter ? total : 0;

  const getStageAggByName = (aggName: string, type: "sum" | "avg") => {
    const aggData = stageAggregations?.data;
    const allAggs = aggData?.[0]?.fields;
    if (allAggs) {
      const aggregation = find(allAggs, (agg) => agg.name === aggName);
      return aggregation ? aggregation?.aggregations?.[type] : 0;
    }
    return 0;
  };

  const buildAggregationsFromItems = (
    filteredItems: DealData[]
  ): GenericAggregations<string> => {
    const aggField = (name: string) => ({
      name,
      aggregations: {
        sum: sumBy(filteredItems, (deal) => (deal[name] as number) ?? 0),
        avg: meanBy(filteredItems, (deal) => (deal[name] as number) ?? 0) || 0,
        min: 0,
        max: 0,
        count: filteredItems.length
      }
    });

    return {
      data: [
        {
          fieldValue: String(id),
          fields: [
            aggField("arr"),
            aggField("mrr"),
            aggField("daysInStage"),
            aggField("openForDays"),
            aggField("committedArr"),
            aggField("committedMrr")
          ]
        }
      ],
      groupedBy: "stage"
    };
  };

  // Memoized because it feeds an effect that writes to board context — a fresh
  // reference on every render would retrigger that effect indefinitely.
  const emptyAggregations = useMemo(() => buildAggregationsFromItems(NO_DEALS), [id]);

  // An excluded stage contributes zeros to the header and the KPI bar rather
  // than the last cached figures.
  const stageAggregations = isStageInFilter
    ? (aggregations as GenericAggregations<string>)
    : emptyAggregations;

  const displayAggregationsLoading = isStageInFilter && isAggregationsLoading;
  const displayAggregationsError = isStageInFilter && isAggregationsError;
  // The count follows the rows query. An excluded stage reports zero by design
  // and has nothing to fail.
  const displayRowsError = isStageInFilter && isError;

  const displayArrMrr = getStageAggByName(arrOrMrrField, "sum");

  useEffect(() => {
    kanbanContext?.setColumnRefetchFunctions((prev) => ({
      ...prev,
      [id]: refetch
    }));
  }, [id]);

  useEffect(() => {
    kanbanContext?.setColumnAggregations((prev) => ({
      ...prev,
      [id]: {
        aggregations: stageAggregations,
        total: stageTotal,
        isLoading: displayAggregationsLoading,
        isError: displayAggregationsError,
        isRowsError: displayRowsError
      }
    }));
  }, [
    stageAggregations,
    stageTotal,
    id,
    displayAggregationsLoading,
    displayAggregationsError,
    displayRowsError
  ]);

  useEffect(() => {
    addColumnDealsToMainState(stageDeals, id);
  }, [stageDeals, id, addColumnDealsToMainState]);

  const handleLoadMore = useCallback(() => {
    if (isStageInFilter && !isFetchingNextPage && hasNextPage && !isError) {
      fetchNextPage();
    }
  }, [isStageInFilter, isFetchingNextPage, hasNextPage, isError, fetchNextPage]);

  const { rootRef: scrollRootRef, sentinelRef: loadMoreSentinelRef } =
    useInfiniteScrollTrigger({
      enabled: !isMinimized,
      canLoadMore:
        isStageInFilter && !isFetchingNextPage && !!hasNextPage && !isError,
      onLoadMore: handleLoadMore,
      itemCount: items.length
    });

  const setColumnRef = useCallback(
    (element: HTMLDivElement | null) => {
      columnRef.current = element;
      scrollRootRef(element);
    },
    [scrollRootRef]
  );

  // Force a reflow when minimized state changes
  useEffect(() => {
    if (columnRef.current) {
      void columnRef.current.offsetHeight;
    }
  }, [isMinimized]);

  const renderMinimizedColumn = () => {
    return (
      <Tooltip key={id}>
        <TooltipTrigger asChild>
          <div
            className="group inline-flex cursor-pointer p-2 rounded-xl"
            onClick={() => onExpandColumn(id)}
            style={{
              backgroundColor: getColorForMilestoneByType(
                milestone,
                "background"
              )
            }}
          >
            <div
              className="w-[5px] h-6 rounded"
              style={{
                backgroundColor: getColorForMilestoneByType(milestone, "border")
              }}
            />
            <div className="group-hover:w-4 group-hover:ml-2 w-0 flex items-center overflow-hidden transition-all duration-200">
              <IconContainer>
                <Expand />
              </IconContainer>
            </div>
          </div>
        </TooltipTrigger>
        <TooltipContent>{title}</TooltipContent>
      </Tooltip>
    );
  };

  const renderExpandedColumn = () => {
    return (
      <>
        <StageCard
          {...column}
          arrMrr={displayArrMrr}
          deals={stageTotal}
          isLoading={
            (isStageInFilter && isLoading) || displayAggregationsLoading
          }
          onMinimizeColumn={onMinimizeColumn}
        />
        <Droppable
          droppableId={id.toString()}
          // A stage the filter excludes renders empty, so a card dropped there
          // would vanish on the next merge — refuse the drop instead.
          isDropDisabled={!isStageInFilter}
          renderClone={(provided, _snapshot, rubric) => (
            <div {...provided.draggableProps} ref={provided.innerRef}>
              <DealCard
                item={items[rubric.source.index]}
                avgDurationWeeks={daysToWeeks(
                  getStageAggByName("daysInStage", "avg")
                )}
                milestone={milestone}
                draggedOverArea={draggedOverArea}
                isDragged={true}
                sourceColumnIndex={sourceColumnIndex}
                onCardClick={() => {}} // No-op during clone render
                isDealDrawerOpen={false}
                dragHandleProps={provided.dragHandleProps}
              />
            </div>
          )}
        >
          {(provided: DroppableProvided) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              className={cn("flex-1 min-h-0 overflow-y-auto")}
              style={{
                transform: "none"
              }}
            >
              {items.map((item, itemIndex) => (
                <Draggable
                  key={item.id}
                  draggableId={item.id}
                  index={itemIndex}
                  // DragDropContext should handle this, but adding for emphasis
                  disableInteractiveElementBlocking
                >
                  {(
                    dragProvided: DraggableProvided,
                    dragSnapshot: DraggableStateSnapshot
                  ) => (
                    <div
                      ref={dragProvided.innerRef}
                      {...dragProvided.draggableProps}
                      style={{
                        ...dragProvided.draggableProps.style,
                        margin: 0
                      }}
                      onClick={
                        dragSnapshot.isDragging
                          ? (e) => e.preventDefault()
                          : undefined
                      }
                    >
                      <DealCard
                        item={item}
                        avgDurationWeeks={daysToWeeks(
                          getStageAggByName("daysInStage", "avg")
                        )}
                        milestone={milestone}
                        draggedOverArea={draggedOverArea}
                        isDragged={dragSnapshot.isDragging}
                        sourceColumnIndex={sourceColumnIndex}
                        onCardClick={(item: DealData | null) =>
                          onCardClick(item)
                        }
                        isDealDrawerOpen={drawerDealId === item.id}
                        dragHandleProps={dragProvided.dragHandleProps}
                      />
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
              <div
                ref={loadMoreSentinelRef}
                className="w-full py-4 text-center"
              >
                {isFetchingNextPage && (
                  <InlineLoader label={t("loading")} className="z-30" />
                )}
              </div>
            </div>
          )}
        </Droppable>
      </>
    );
  };

  return (
    <div
      ref={setColumnRef}
      className={cn(
        "relative flex flex-col transition-all duration-200",
        isMinimized ? "w-auto mr-2" : "w-80 min-w-80 mr-4"
      )}
    >
      {isMinimized ? renderMinimizedColumn() : renderExpandedColumn()}
    </div>
  );
};

export default KanbanColumn;
